import axios from "axios";

const ENV_BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// SEC-10 cookie-auth fix (incognito sign-in loop): the `session_token` cookie is
// httpOnly + SameSite=None. If the SPA calls the API on a DIFFERENT origin than
// the page it's served from, that cookie becomes a THIRD-PARTY cookie — which
// browsers block in incognito/private mode (and are phasing out generally). The
// symptom: login succeeds (CORS lets the body through) but the cookie is never
// stored, so the next protected request 401s and the global handler bounces the
// user back to /login in a loop.
//
// The Emergent ingress routes `/api` to the backend on the SAME origin the app
// is served from. So when the page origin DIFFERS from REACT_APP_BACKEND_URL
// (e.g. the production SPA on https://startupotter.com vs the baked-in backend
// https://otter-phase.emergent.host), we call the API same-origin and keep the
// cookie FIRST-PARTY. When they already match (e.g. preview), behaviour is
// unchanged and the env value is used as-is. Non-browser contexts fall back to
// the env value.
function resolveApiOrigin() {
  if (typeof window === "undefined" || !window.location || !window.location.origin) {
    return ENV_BACKEND;
  }
  const pageOrigin = window.location.origin;
  try {
    if (ENV_BACKEND && new URL(ENV_BACKEND).origin === pageOrigin) {
      return ENV_BACKEND; // already same-origin (preview) — preserve existing behaviour
    }
  } catch (_) {
    /* malformed env URL — fall through to same-origin */
  }
  return pageOrigin; // cross-origin mismatch — use same-origin /api (first-party cookie)
}

export const API_ORIGIN = resolveApiOrigin();
export const API_BASE = `${API_ORIGIN}/api`;

// SEC-10: cookie-only web auth — the httpOnly `session_token` cookie (sent via
// withCredentials) authenticates every request. No token is stored in the browser.
const api = axios.create({ baseURL: API_BASE, withCredentials: true });

// SEC-09: a handler registered by AuthProvider that clears auth state and
// redirects when a PROTECTED request returns 401 (the server-side session was
// invalidated mid-session: self-delete, admin delete, session-TTL expiry, or a
// password reset on another device). Without this, the SPA kept `user` truthy
// and data-gated pages hung forever on a swallowed 401.
let onUnauthorized = null;
export function setUnauthorizedHandler(fn) {
  onUnauthorized = fn;
}

// Auth endpoints intentionally handle their own 401s (bad login credentials,
// the initial /auth/me probe, restore-code checks, password reset, etc.). Never
// trigger the global handler for these — that also prevents redirect loops on
// the login / restore pages.
const isAuthPath = (url = "") => url === "/auth" || url.startsWith("/auth/");

api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    if (status === 401 && !isAuthPath(url) && typeof onUnauthorized === "function") {
      try { onUnauthorized(); } catch (_) { /* ignore */ }
    }
    return Promise.reject(error);
  }
);

export function fileUrl(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${API_ORIGIN}${url}`;
}

export default api;
